import { drawSlotDesign, type LoadedImages } from '../render-core';
import { pxPerMm, type SlotDesign } from '../design-data';
import { pickLocale, resolveTemplateSlots, type LocalizedContent } from '../templates/types';
import { resolveImageUrl } from 'src/utils/resolve-image-url';

/**
 * Live, localized template previews for the picker.
 *
 * Every template has ONE pre-rendered thumbnail, rendered by the admin tool
 * with Slovenian text, so the Slovak shop's "Vyberte šablónu" showed Slovenian
 * invitations. The layers carry every language (`content: {sl, sk, pl, …}`),
 * so instead of one image per locale the picker draws the template itself
 * with the same render-core the print export uses (drawSlotDesign), at
 * thumbnail size, in the visitor's language.
 *
 * Cost control, because the picker holds ~50 templates and the phone matters
 * most:
 *   - callers only ask once a tile is (nearly) on screen (IntersectionObserver);
 *   - at most MAX_CONCURRENT renders run at a time;
 *   - fonts are loaded per face actually used, not the whole editor list;
 *   - signed sticker URLs, decoded images and finished previews are cached for
 *     the page's lifetime, so reopening the sheet costs nothing;
 *   - the output is a small JPEG object URL, not a live <canvas> per tile.
 *
 * When the visitor's text is identical to the Slovenian text the thumbnail
 * already shows (the Slovenian shop), no live render is needed at all.
 */

/** The locale the admin thumbnails are rendered in. */
export const THUMBNAIL_LOCALE = 'sl';

const SAMPLE = 'AaÁáÄäČčĆćĐđĘęŁłŃńŠšŚśŻżŽžŇňŤťĽľŔŕ0123456789';
const MAX_CONCURRENT = 2;

/**
 * Does `slot`, shown in `locale`, read differently from the pre-rendered
 * (Slovenian) thumbnail? Only text changes between locales, so compare every
 * text layer's resolved content.
 */
export function differsFromThumbnailLocale(slot: SlotDesign | undefined, locale: string): boolean {
  if (!slot) return false;
  return (slot.layers ?? []).some((l) => {
    if (l.type !== 'text') return false;
    const content = l.content as LocalizedContent;
    if (typeof content === 'string') return false;
    return pickLocale(content, locale) !== pickLocale(content, THUMBNAIL_LOCALE);
  });
}

/** CSS font shorthands (one per family + weight) a resolved slot draws with. */
export function fontFacesFor(slot: SlotDesign): string[] {
  const faces = new Set<string>();
  for (const l of slot.layers ?? []) {
    if (l.type !== 'text' || !l.font) continue;
    faces.add(`${l.bold ? '700' : '400'} 16px "${l.font}"`);
  }
  return [...faces];
}

/** Image keys (stickers, paper photo) a slot needs to be drawn. */
export function imageKeysFor(slot: SlotDesign): string[] {
  const keys = new Set<string>();
  if (slot.background?.image) keys.add(slot.background.image);
  for (const l of slot.layers ?? []) {
    if (l.type === 'image' && l.sourceKey && !l.hidden) keys.add(l.sourceKey);
  }
  return [...keys];
}

/** The dpi that draws a `widthMm` page `widthPx` pixels wide. */
export function previewDpi(widthMm: number, widthPx: number): number {
  return widthMm > 0 ? (widthPx / widthMm) * 25.4 : 72;
}

// ── Caches (page lifetime) ───────────────────────────────────────────────────

const fontCache = new Map<string, Promise<void>>();
const imageCache = new Map<string, Promise<HTMLImageElement | null>>();
const previewCache = new Map<string, Promise<string | null>>();

function loadFont(face: string): Promise<void> {
  let p = fontCache.get(face);
  if (!p) {
    p =
      typeof document !== 'undefined' && document.fonts
        ? document.fonts.load(face, SAMPLE).then(
            () => undefined,
            () => undefined,
          )
        : Promise.resolve();
    fontCache.set(face, p);
  }
  return p;
}

function loadImage(key: string): Promise<HTMLImageElement | null> {
  let p = imageCache.get(key);
  if (!p) {
    p = (async () => {
      const url = await resolveImageUrl(key);
      if (!url) return null;
      return new Promise<HTMLImageElement | null>((resolve) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });
    })();
    // A failed lookup is not cached forever: drop it so a later tile retries.
    p.then((img) => {
      if (!img) imageCache.delete(key);
    });
    imageCache.set(key, p);
  }
  return p;
}

// ── Concurrency gate ─────────────────────────────────────────────────────────

let running = 0;
const waiting: Array<() => void> = [];

async function withSlot<T>(fn: () => Promise<T>): Promise<T> {
  if (running >= MAX_CONCURRENT) {
    await new Promise<void>((resolve) => waiting.push(resolve));
  }
  running++;
  try {
    return await fn();
  } finally {
    running--;
    waiting.shift()?.();
  }
}

/**
 * Render one template slot (raw, with locale maps) in `locale`, `widthPx`
 * wide, onto `canvas` (page size, no bleed — the framing the admin thumbnail
 * uses). Resolves to an object URL of a JPEG, or null when it could not be
 * drawn (the caller keeps the static thumbnail).
 *
 * `cacheKey` must identify the template version (code + updatedAt or the
 * thumbnail URL) — the result is memoised on it plus locale and size.
 */
export function renderTemplatePreview(
  rawSlot: SlotDesign,
  locale: string,
  widthPx: number,
  cacheKey: string,
  canvas?: { widthMm: number; heightMm: number },
): Promise<string | null> {
  const w = Math.max(32, Math.round(widthPx));
  const key = `${cacheKey}|${locale}|${w}|${canvas?.widthMm ?? ''}x${canvas?.heightMm ?? ''}`;
  const cached = previewCache.get(key);
  if (cached) return cached;

  const job = withSlot(async () => {
    const [slot] = resolveTemplateSlots([rawSlot], locale);
    const design: SlotDesign = canvas
      ? { ...slot, canvas: { ...slot.canvas, widthMm: canvas.widthMm, heightMm: canvas.heightMm } }
      : slot;
    const { widthMm, heightMm } = design.canvas;
    if (!(widthMm > 0 && heightMm > 0)) return null;

    await Promise.all(fontFacesFor(design).map(loadFont));
    const loaded: LoadedImages = {};
    await Promise.all(
      imageKeysFor(design).map(async (k) => {
        const img = await loadImage(k);
        if (img) loaded[k] = img;
      }),
    );

    const dpi = previewDpi(widthMm, w);
    const ppm = pxPerMm(dpi);
    const el = document.createElement('canvas');
    el.width = Math.round(widthMm * ppm);
    el.height = Math.round(heightMm * ppm);
    const ctx = el.getContext('2d');
    if (!ctx) return null;
    // White paper under everything — the thumbnail's own framing.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, el.width, el.height);
    drawSlotDesign(ctx, design, dpi, loaded, '', { includeBackground: true });

    const blob = await new Promise<Blob | null>((resolve) => {
      try {
        el.toBlob(resolve, 'image/jpeg', 0.86);
      } catch {
        // A tainted canvas (sticker served without CORS) cannot be exported.
        resolve(null);
      }
    });
    // Release the backing store right away; only the JPEG is kept.
    el.width = 0;
    el.height = 0;
    return blob ? URL.createObjectURL(blob) : null;
  }).catch(() => null);

  previewCache.set(key, job);
  // Do not pin a failure: a later attempt (e.g. after a network blip) may work.
  job.then((url) => {
    if (!url) previewCache.delete(key);
  });
  return job;
}
